import type {
  FeesAndExpenses,
  MutualFundKeyStats,
  Risk,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { MutualFundsChartSection } from '#/components/charts/MutualFundsChartSection';
import { DefinitionTooltip } from '#/components/content/DefinitionTooltip';
import { DisclosureStatementButton } from '#/components/content/DisclosureStatement';
import { Icon } from '#/components/ui/icons';
import type { RangeSegment } from '#/components/ui/RangeSegments';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { useDefinitionDrawer } from '#/context/DefinitionDrawerContext';
import { formatCurrency, formatDecimal, formatPercent } from '#/utils/format';

import { RangeSegments } from '../ui/RangeSegments';
import { CategoryComparedReturn } from './CategoryComparedReturn';
import { StatGroup, StatRow, valueClass } from './StatGroup';

const BETA_SEGMENTS: RangeSegment[] = [
  {
    label: '< -1',
    min: null,
    max: -1,
    description: 'Moves inversely and more extremely than the market',
  },
  {
    label: '-1 to 0',
    min: -1,
    max: 0,
    description: 'Moves inversely to the market',
  },
  {
    label: '0 to 1',
    min: 0,
    max: 1,
    // `1` lands here (at the top), not in the `> 1` segment.
    maxInclusive: true,
    description: (value) =>
      value === 1
        ? 'As volatile as the market'
        : 'Less volatile than the market',
  },
  {
    label: '> 1',
    min: 1,
    max: null,
    description: 'More volatile than the market',
  },
];

const SHARPE_SEGMENTS: RangeSegment[] = [
  {
    label: '< 1',
    min: null,
    max: 1,
    description: 'Likely low returns per risk',
  },
  {
    label: '1 to 2',
    min: 1,
    max: 2,
    maxInclusive: true,
    description: 'Acceptable returns per risk',
  },
  {
    label: '2 to 3',
    min: 2,
    max: 3,
    maxInclusive: true,
    description: 'Good returns per risk',
  },
  {
    label: '> 3',
    min: 3,
    max: null,
    description: 'Exceptional returns per risk',
  },
];

interface MutualFundPerformanceSectionProps {
  symbol: string;
  keyStats: MutualFundKeyStats | null | undefined;
  feesAndExpenses: FeesAndExpenses | null | undefined;
  risk: Risk | null | undefined;
  /** Morningstar category the fund belongs to; drives the return-row baselines. */
  categoryCode?: string | null;
  /** The category's display name, used to label the comparison baseline. */
  categoryName?: string | null;
  date: string | null | undefined;
  description?: string | null;
  priceIntro?: string | null;
  riskIntro?: string | null;
}

export function MutualFundPerformanceSection({
  symbol,
  keyStats,
  feesAndExpenses,
  risk,
  categoryCode,
  categoryName,
  date,
  description,
  priceIntro,
  riskIntro,
}: MutualFundPerformanceSectionProps) {
  const displaySymbol = symbol.toUpperCase();
  const { termDescription } = useDefinitionDrawer();

  if (!keyStats && !feesAndExpenses && !risk) {
    return null;
  }

  return (
    <section
      id="performance"
      className="flow flow-32 scroll-mt-(--scroll-offset)"
    >
      <SectionHeader
        title="Performance"
        date={date}
        description={description}
        actions={<DisclosureStatementButton />}
      />

      <div className="-mx-gap sm:border-divider-subtle divide-divider-subtle divide-y sm:mx-0 sm:rounded sm:border">
        <MutualFundsChartSection
          symbol={symbol}
          previousClose={keyStats?.previous_close}
        />

        {keyStats && (
          <StatGroup id="key-stats" title="Key Stats">
            <StatRow
              label={
                <DefinitionTooltip termSlug="expense-ratio">
                  Expense Ratio
                </DefinitionTooltip>
              }
              description={termDescription('expense-ratio')}
              value={formatPercent(keyStats.expense_ratio)}
            />
            <StatRow
              label={
                <span>
                  Previous{' '}
                  <DefinitionTooltip termSlug="previous-close">
                    Close
                  </DefinitionTooltip>
                </span>
              }
              description={termDescription('previous-close')}
              value={formatCurrency(keyStats.previous_close)}
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="5-year-trailing-return">
                  5 Year Annualized Return
                </DefinitionTooltip>
              }
              description={termDescription('5-year-trailing-return')}
              value={
                keyStats.return_5Y_annualized != null && (
                  <CategoryComparedReturn
                    symbol={displaySymbol}
                    value={keyStats.return_5Y_annualized}
                    averageKey="return_5Y_annualized"
                    categoryCode={categoryCode}
                    categoryName={categoryName}
                  />
                )
              }
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="1-year-trailing-return">
                  1 Year Trailing Return
                </DefinitionTooltip>
              }
              description={termDescription('1-year-trailing-return')}
              value={
                keyStats.trailing_return_1Y != null && (
                  <CategoryComparedReturn
                    symbol={displaySymbol}
                    value={keyStats.trailing_return_1Y}
                    averageKey="trailing_return_1Y"
                    categoryCode={categoryCode}
                    categoryName={categoryName}
                  />
                )
              }
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="6-month-return">
                  6 Month Return
                </DefinitionTooltip>
              }
              description={termDescription('6-month-return')}
              value={
                keyStats.return_6M != null && (
                  <CategoryComparedReturn
                    symbol={displaySymbol}
                    value={keyStats.return_6M}
                    averageKey="return_6M"
                    categoryCode={categoryCode}
                    categoryName={categoryName}
                  />
                )
              }
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="ytd-return">
                  YTD Return
                </DefinitionTooltip>
              }
              description={termDescription('ytd-return')}
              value={
                keyStats.return_YTD != null && (
                  <CategoryComparedReturn
                    symbol={displaySymbol}
                    value={keyStats.return_YTD}
                    averageKey="return_YTD"
                    categoryCode={categoryCode}
                    categoryName={categoryName}
                  />
                )
              }
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="dividend-yield-forward-0">
                  Dividend Yield (Forward)
                </DefinitionTooltip>
              }
              description={termDescription('dividend-yield-forward-0')}
              value={formatPercent(keyStats.dividend_yield_forward)}
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="price-earnings-pe-ratio-0">
                  Price-to-Earnings (PE) Ratio
                </DefinitionTooltip>
              }
              description={termDescription('price-earnings-pe-ratio-0')}
              value={formatDecimal(keyStats.pe_ratio)}
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="price-sales-0">
                  Price to Sales
                </DefinitionTooltip>
              }
              description={termDescription('price-sales-0')}
              value={formatDecimal(keyStats.price_to_sales)}
            />
          </StatGroup>
        )}

        {feesAndExpenses && (
          <StatGroup
            id="price"
            title="Fees and Expenses"
            description={priceIntro ?? undefined}
          >
            <StatRow
              label={
                <DefinitionTooltip termSlug="annual-operating-expenses">
                  Annual Operating Expenses
                </DefinitionTooltip>
              }
              description={termDescription('annual-operating-expenses')}
              value={formatPercent(feesAndExpenses.annual_operating_expenses)}
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="adjusted-expense-ratio-0">
                  Adjusted Expense Ratio
                </DefinitionTooltip>
              }
              description={termDescription('adjusted-expense-ratio-0')}
              value={formatPercent(feesAndExpenses.adj_expense_ratio)}
              date={date ?? undefined}
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="net-expense-ratio">
                  Net Expense Ratio
                </DefinitionTooltip>
              }
              description={termDescription('net-expense-ratio')}
              value={formatPercent(feesAndExpenses.net_expense_ratio)}
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="minimum-initial-investment">
                  Minimum Initial Investment
                </DefinitionTooltip>
              }
              description={termDescription('minimum-initial-investment')}
              value={formatCurrency(feesAndExpenses.min_initial_investment)}
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="front-load">
                  Front Load
                </DefinitionTooltip>
              }
              description={termDescription('front-load')}
              value={
                feesAndExpenses.front_load != null && (
                  <span className="inline-flex items-center gap-6">
                    {formatPercent(feesAndExpenses.front_load)}
                    <DefinitionTooltip
                      termSlug="front-load"
                      enhancedDefinition=""
                      className="leading-0"
                      definition={
                        <div className="leading-normal font-normal">
                          <p className="font-bold">Front load schedule</p>
                          <p>{'5.75% ($0 - <$25,000)'}</p>
                          <p>{'5% ($25,000 - <$50,000)'}</p>
                          <p>{'4.5% ($50,000 - <$100,000)'}</p>
                          <p>{'3.5% ($100,000 - <$250,000)'}</p>
                          <p>{'2.5% ($250,000 - <$500,000)'}</p>
                          <p>{'2% ($500,000 - <$750,000)'}</p>
                          <p>{'1.5% ($750,000 - <$1,000,000)'}</p>
                          <p>{'0% (>$1,000,000)'}</p>
                        </div>
                      }
                    >
                      <Icon
                        id="info"
                        size={16}
                        className="text-button-link-fg block"
                      />
                    </DefinitionTooltip>
                  </span>
                )
              }
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="deferred-load">
                  Deferred Load
                </DefinitionTooltip>
              }
              description={termDescription('deferred-load')}
              value={formatPercent(feesAndExpenses.deferred_load)}
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="redemption-load">
                  Redemption Load
                </DefinitionTooltip>
              }
              description={termDescription('redemption-load')}
              value={formatPercent(feesAndExpenses.redemption_fee)}
            />
          </StatGroup>
        )}

        {risk && (
          <StatGroup
            id="risk"
            title="Risk"
            description={riskIntro ?? undefined}
          >
            <StatRow
              label={
                <DefinitionTooltip termSlug="beta">Beta</DefinitionTooltip>
              }
              description={termDescription('beta')}
              value={
                risk.beta != null && (
                  <RangeSegments
                    className={valueClass}
                    value={risk.beta}
                    segments={BETA_SEGMENTS}
                    // Truncate toward zero so a beta just under an integer
                    // (e.g. 0.998) doesn't round up across the segment boundary.
                    format={(value) =>
                      formatDecimal(value, { roundingMode: 'trunc' })
                    }
                  />
                )
              }
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="sharpe-ratio">
                  Sharpe Ratio
                </DefinitionTooltip>
              }
              description={termDescription('sharpe-ratio')}
              value={
                risk.sharpe_ratio != null && (
                  <RangeSegments
                    className={valueClass}
                    value={risk.sharpe_ratio}
                    segments={SHARPE_SEGMENTS}
                    // Truncate toward zero so a ratio just under an integer
                    // (e.g. 0.998) doesn't round up across the segment boundary.
                    format={(value) =>
                      formatDecimal(value, { roundingMode: 'trunc' })
                    }
                  />
                )
              }
            />
            <StatRow
              label={
                <DefinitionTooltip termSlug="standard-deviation">
                  Standard Deviation
                </DefinitionTooltip>
              }
              description={termDescription('standard-deviation')}
              value={formatDecimal(risk.standard_deviation)}
            />
          </StatGroup>
        )}
      </div>
    </section>
  );
}
